import * as THREE from "three";
import type { Vector3, Quaternion } from "../types/primitives";

/**
 * Convert degrees to radians
 */
export const degreesToRadians = (degrees: number): number => degrees * (Math.PI / 180);

/**
 * Convert radians to degrees
 */
export const radiansToDegrees = (radians: number): number => radians * (180 / Math.PI);

/**
 * Convert an array of degrees to radians
 */
export const degreesArrayToRadians = (degreesArray: number[]): number[] =>
  degreesArray.map((deg: number) => degreesToRadians(deg));

/**
 * Convert an array of radians to degrees
 */
export const radiansArrayToDegrees = (radiansArray: number[]): number[] =>
  radiansArray.map((rad: number) => radiansToDegrees(rad));

// THREE remains the shared conversion implementation used by viewer transform code.
/**
 * Convert Euler angles in degrees to quaternion array
 */
export const eulerDegreesToQuaternion = (eulerDegrees: Vector3): Quaternion => {
  const eulerRadians = degreesArrayToRadians(eulerDegrees);
  const euler = new THREE.Euler(eulerRadians[0], eulerRadians[1], eulerRadians[2], "XYZ");
  const quaternion = new THREE.Quaternion().setFromEuler(euler);
  return [quaternion.w, quaternion.x, quaternion.y, quaternion.z];
};

// Keep quaternion/euler conversion behavior aligned with the viewer's THREE math.
/**
 * Convert quaternion array to Euler angles in degrees
 */
export const quaternionToEulerDegrees = (quaternion: Quaternion): Vector3 => {
  const threeQuat = new THREE.Quaternion(
    quaternion[1],
    quaternion[2],
    quaternion[3],
    quaternion[0]
  );
  const euler = new THREE.Euler().setFromQuaternion(threeQuat, "XYZ");
  return radiansArrayToDegrees([euler.x, euler.y, euler.z]) as Vector3;
};
